import {EventEmitter, Injectable} from "@angular/core";
import {
  ActionSheetButton,
  ActionSheetController,
  AlertButton,
  AlertController,
  ModalController, NavController,
  Platform,
  ToastController
} from "@ionic/angular";
import {Browser} from '@capacitor/browser';
import {Share} from "@capacitor/share";
import {ShareParams} from "../model/share-params.model";
import {LocationService} from "./location.service";
import {HttpClient} from "@angular/common/http";
import {profileType, serviceSectionType} from "../types/types";
import {OnlyMobileComponent} from "../components/only-mobile/only-mobile.component";
import {TranslateService} from "@ngx-translate/core";
import {environment} from "../../../environments/environment";
import {Moment} from "moment";
import {CapacitorCalendar} from "@ebarooni/capacitor-calendar";
import {CalendarOptions} from "../model/calendar.model";
import {AppLauncher} from '@capacitor/app-launcher';
import {Subject} from "rxjs";


@Injectable({
  providedIn: 'root'
})
export class CommonService {

  private _profileTypeKeyStorage = "profileType";
  private _profileType: profileType = undefined;

  private mainLoader: boolean;

  changeMainLoader = new EventEmitter();
  changeTransparentLoader = new EventEmitter();
  openContactDrawer = new Subject();

  constructor(private alertController: AlertController,
              private http: HttpClient,
              private toastController: ToastController,
              private modalController: ModalController,
              private navController: NavController,
              private translateService: TranslateService,
              private actionSheetController: ActionSheetController,
              private platform: Platform) {
    this.profileType = localStorage.getItem(this._profileTypeKeyStorage) ? localStorage.getItem(this._profileTypeKeyStorage) as profileType : undefined;
  }

  static isStage() {
    return environment.host.indexOf('be-stage') != -1;
  }

  get profileType(): profileType {
    return this._profileType;
  }

  set profileType(value: profileType) {
    this._profileType = value;
    if (value) {
      localStorage.setItem(this._profileTypeKeyStorage, value);
    } else {
      localStorage.removeItem(this._profileTypeKeyStorage);
    }
  }

  onToggleMainLoader() {

    this.mainLoader = !this.mainLoader;

    if (!this.mainLoader) {
      setTimeout(() => {
        this.changeMainLoader.emit(this.mainLoader);
      }, 800);
    } else {
      this.changeMainLoader.emit(this.mainLoader);
    }

  }

  onToggleTransparentLoader() {

    this.mainLoader = !this.mainLoader;

    if (!this.mainLoader) {
      setTimeout(() => {
        this.changeTransparentLoader.emit(this.mainLoader);
      }, 800);
    } else {
      this.changeTransparentLoader.emit(this.mainLoader);
    }

  }

  elaborateLink(link: string){
    if(link?.includes('https://andriasmart.fivelab.it')){
      this.navController.navigateForward(link?.replace('https://andriasmart.fivelab.it',''));
    } else {
      this.onOpenLinkBrowser(link);
    }
  }

  onOpenLinkBrowser = async (url: string, type?: string) => {
    if (this.platform.platforms().indexOf('mobileweb') != -1) {
      window.open(url, '_blank');
    } else {
      return await Browser.open({url});
    }
  };

  async openDownloadLink(androidAppId: string, iosAppId: string) {

    let url: string;
    const androidUrl = 'https://play.google.com/store/apps/details?id=' + androidAppId;
    const iosUrl = 'https://apps.apple.com/app/id' + iosAppId;

    if (this.platform.is('mobileweb')) {

      const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
      if (/android/i.test(userAgent)) {
        url = androidUrl;
      } else if (/iPad|iPhone|iPod/.test(userAgent) && !window['MSStream']) {
        url = iosUrl;
      }

    } else {

      if (this.platform.platforms().indexOf('android') != -1) {
        url = androidUrl;
      } else {
        url = iosUrl;
      }

    }

    window.open(url, '_system', 'location=yes');
  }

  initiateCall(phoneNumber: string) {
    window.location.href = `tel:${phoneNumber}`;
  }

  openGoogleMapsByName(name: string, startLat?: number, startLng?: number) {
    const baseUrl = "https://www.google.com/maps/dir/?api=1";
    const destination = `destination=${encodeURIComponent(name)}`;

    // Verifica che startLat e startLng siano valori numerici validi
    const hasStartCoords = typeof startLat === 'number' && typeof startLng === 'number' &&
      !isNaN(startLat) && !isNaN(startLng);

    let mapsUrl;

    if (hasStartCoords) {
      // Non conoscendo le coordinate della destinazione, non possiamo calcolare la distanza:
      // lasciamo che sia Google Maps a scegliere il travel mode di default
      mapsUrl = `${baseUrl}&origin=${startLat},${startLng}&${destination}`;
    } else {
      mapsUrl = `${baseUrl}&${destination}`;
    }

    this.onOpenLinkBrowser(mapsUrl);
  }

  openGoogleMaps(destinationLat, destinationLng, startLat?, starLng?) {
    const baseUrl = "https://www.google.com/maps/dir/?api=1";
    const destination = `destination=${destinationLat},${destinationLng}`;

    // Verifica che startLat e starLng siano valori numerici validi
    const hasStartCoords = typeof startLat === 'number' && typeof starLng === 'number' &&
      !isNaN(startLat) && !isNaN(starLng);

    let mapsUrl;

    if (hasStartCoords) {
      const distance = LocationService.getDistanceFromLatLonInKm(destinationLat, destinationLng, startLat, starLng);
      const travelMode = distance > 2 ? "travelmode=driving" : "travelmode=walking"; // Limite di 2 km (corretto da 2000 a 2)
      mapsUrl = `${baseUrl}&origin=${startLat},${starLng}&${destination}&${travelMode}`;
    } else {
      // Se non ci sono coordinate di partenza, apri solo la destinazione
      mapsUrl = `${baseUrl}&destination=${destinationLat},${destinationLng}`;
    }

    this.onOpenLinkBrowser(mapsUrl);
  }

  async addEventCalendar(calendar: CalendarOptions) {

    const result = await CapacitorCalendar.createEventWithPrompt({
      title: calendar?.name,
      location: calendar?.location,
      startDate: calendar.startDate,
      alerts: [-60],
      endDate: calendar.endDate // +1 ora in ms
    });

  }

  openStoreLink() {
    this.openDownloadLink('it.andriasmart', '6747346881').then();
  }

  async showToast(message: string) {
    const toast = await this.toastController.create({
      message,
      duration: 2000,
      position: 'bottom',
      cssClass: 'app-toast'
    });
    toast.present();
  }

  async openGoogleMapsNative(destinations: string[]) {
    const destinationAddress = destinations.join(' ,-');
    const googleMapsScheme = `comgooglemaps://?daddr=${encodeURIComponent(destinationAddress)}&directionsmode=driving`;
    const webUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destinationAddress)}`;

    console.log('webUrl', webUrl);
    console.log('googleMapsScheme', googleMapsScheme);

    if (!this.isMobile()) {
      window.open(webUrl, '_system');
      return;
    }

    // Controlla se l'app Google Maps è installata
    const canOpen = await AppLauncher.canOpenUrl({url: googleMapsScheme});
    if (canOpen.value) {
      // Apri Google Maps nativo
      await AppLauncher.openUrl({url: googleMapsScheme});
    } else {
      // Fallback: apri la versione web di Google Maps
      await AppLauncher.openUrl({url: webUrl});
    }
  }

  static stripHtml(html: string): string {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  static buildDeepLink(url: string) {
    return environment.appDeepLink + url;
  }

  async share(params: ShareParams) {
    return await Share.share({
      title: params.title,
      text: params.text,
      url: params.url,
      dialogTitle: params.dialogTitle,
    });
  }

  async presentAlertMsg(headerTxt: string, messageTxt: string, buttons: AlertButton[] = []) {

    const header = this.translateService.instant(headerTxt);
    const message = this.translateService.instant(messageTxt);

    if (buttons.length == 0) {
      buttons.push({
        text: 'Ok',
        role: 'cancel',
        handler: (blah) => {
        }
      });
    }

    const alert = await this.alertController.create({
      header,
      message,
      cssClass: 'confirm-alert',
      buttons
    });

    await alert.present();
  }

  sendMessageToTelegram(text: string) {
    return this.http.post("https://api.telegram.org/bot5698606397:AAF8Hcs-vDWM_JbTtpPsqlAfJeANeii-pmo/sendMessage", {
      "chat_id": "-1001574544970",
      text,
      "disable_notification": true
    });
  }

  isMobile() {
    return this.platform.platforms().indexOf('mobileweb') == -1 &&
      (this.platform.platforms().indexOf('ios') != -1 || this.platform.platforms().indexOf('android') != -1);
  }

  isIos() {
    return this.platform.platforms().indexOf('ios') !== -1 && this.isMobile();
  }

  isAndroid() {
    return this.platform.platforms().indexOf('android') !== -1 && this.isMobile();
  }

  hasNotch() {
    return parseInt(getComputedStyle(document.documentElement).getPropertyValue('padding-top')) || 0;
  }

  async presentMobileViewAdvice() {

    var isDesktop = this.platform.is('desktop');

    if (isDesktop) {

      const modal = await this.modalController.create({
        component: OnlyMobileComponent
      });

      return await modal.present();
    }

  }

  getDateTimeLabel(diffDays: number, hours: string, format: string, dateMoment: Moment) {

    if (diffDays == 0) {
      return this.translateService.instant('oggi') + hours;
    } else if (diffDays == 1) {
      return this.translateService.instant('ieri') + hours;
    } else if (diffDays === -1) {
      return this.translateService.instant('domani') + hours;
    } else if (diffDays > 1 && diffDays <= 6) {
      return this.translateService.instant('Tempo_fa', {dayNumber: diffDays});
    } else {
      return dateMoment.format(format);
    }

  }

  async onOpenActionSheetOptions(buttons: ActionSheetButton[], header: string = 'Azioni disponibili') {

    return new Promise<void>(async (resolve, reject) => {

      const actionSheet = await this.actionSheetController.create({
        header,
        buttons
      });

      await actionSheet.present();

      actionSheet.onDidDismiss().then((res) => {

        const selected = res?.data?.action;
        resolve(selected);
      });

    });

  }

  sendEmail(email: string, subject: string, body: string) {
    const _subject = encodeURIComponent(subject);
    const _body = encodeURIComponent(body);

    // Apri il client di posta con mailto
    window.open(`mailto:${email}?subject=${_subject}&body=${_body}`, '_system');
  }
}
